import AddQuoteLineModal from "@hardware/Overrides/Components/AddQuoteLineModal";
import { removeQuoteLine, updateQuoteLineNote } from "@hardware/Services/UpdateQuoteStatusService";
import { colorStrings, mediaQueriesBP } from "@hardware/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateQuote from "@insite/client-framework/Store/Data/Quotes/Handlers/UpdateQuote";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import setQuoteLineForCalculation from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SetQuoteLineForCalculation";
import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import RfqQuoteDetailsQuotedPricing, {
    RfqQuoteDetailsQuotedPricingStyles,
} from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import RfqQuoteDetailsQuoteLineCalculator from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuoteLineCalculator";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Percent from "@insite/mobius/Icons/Percent";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    quoteLineForCalculation: state.pages.rfqQuoteDetails.quoteLineForCalculation,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateQuote,
    setQuoteLineForCalculation,
    updateQuoteLine,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsSalesRepProductListStyles {
    mainWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    headerContainer?: GridContainerProps;
    countGridItem?: GridItemProps;
    countLabelText?: TypographyPresentationProps;
    countValueText?: TypographyPresentationProps;
    quoteAllGridItem?: GridItemProps;
    quoteAllButton?: ButtonPresentationProps;
    itemContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    quoteLineInfoGridItem?: GridItemProps;
    quoteLineInfoCustomGridItem?: GridItemProps;
    quoteLineInfoContainer?: GridContainerProps;
    quoteLineInfoCustomContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    infoCenterColumn?: GridItemProps;
    infoContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    uomGridItem?: GridItemProps;
    uomHeadingAndText?: SmallHeadingAndTextStyles;
    quotedPricingGridItem?: GridItemProps;
    quotedPricingText?: TypographyPresentationProps;
    quotedPricingStyles?: RfqQuoteDetailsQuotedPricingStyles;
    infoRightColumn?: GridItemProps;
    quoteLineButton?: ButtonPresentationProps;
    quoteCalculationModal?: ModalPresentationProps;
    quoteCalculationContainer?: GridContainerProps;
    quoteCalculationHeaderGridItem?: GridItemProps;
    quoteCalculationHeaderText?: TypographyPresentationProps;
    quoteCalculationMethodGridItem?: GridItemProps;
    quoteCalculationMethodSelect?: SelectProps;
    quoteCalculationPercentGridItem?: GridItemProps;
    quoteCalculationPercentTextField?: TextFieldPresentationProps;
    quoteCalculationPercentIcon?: IconPresentationProps;
    quoteCalculationErrorGridItem?: GridItemProps;
    quoteCalculationErrorText?: TypographyPresentationProps;
    quoteCalculationCancelGridItem?: GridItemProps;
    quoteCalculationCancelButton?: ButtonPresentationProps;
    quoteCalculationApplyAllGridItem?: GridItemProps;
    quoteCalculationApplyAllButton?: ButtonPresentationProps;
    quoteLineCalculationModal?: ModalPresentationProps;
}

export interface VerndaleRfqQuoteDetailsSalesRepProductListStyles extends RfqQuoteDetailsSalesRepProductListStyles {
    addQuoteLineItemButton?: ButtonPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    orderLineNotesGridItem?: GridItemProps;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    toggleNotesLink?: LinkPresentationProps;
    notesTextArea?: TextAreaProps;
    spinnerWrapper?: InjectableCss;
}

export const rfqQuoteDetailsSalesRepProductListStyles: VerndaleRfqQuoteDetailsSalesRepProductListStyles = {
    headerWrapper: {
        css: css`
            padding-bottom: 15px;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    headerContainer: { gap: 20 },
    countGridItem: {
        width: 6,
        css: css`
            align-items: center;
        `,
    },
    countLabelText: {
        weight: 600,
        css: css`
            margin-left: 5px;
        `,
    },
    countValueText: { weight: 600 },
    quoteAllGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
        `,
    },
    itemContainer: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: { width: 2 },
    quoteLineInfoGridItem: { width: 10 },
    /* Verndale customization */
    quoteLineInfoCustomGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 10,
    },
    quoteLineInfoContainer: { gap: 20 },
    quoteLineInfoCustomContainer: {
        gap: 0,
        css: css`
            padding-top: 10px;

            .file-link {
                height: auto;
                color: ${getColor(colorStrings.primaryMain)};
                min-width: 180px;
                width: 100%;
                display: flex;
                justify-content: space-between;
                gap: 10px;
                font-size: 14px;
                font-weight: 700;

                ${mediaQueriesBP.maxMD} {
                    height: auto;
                }
            }
        `,
    },
    /* End verndale customization */
    infoLeftColumn: { width: [12, 12, 12, 12, 5] },
    infoLeftColumnContainer: { gap: 20 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    infoCenterColumn: { width: [12, 12, 12, 6, 4] },
    infoContainer: { gap: 20 },
    quantityGridItem: { width: 6 },
    quantityHeadingAndText: {
        heading: {
            weight: "bold",
            size: 14,
            transform: "none",
        },
    },
    uomGridItem: { width: 6 },
    uomHeadingAndText: {
        heading: {
            weight: "bold",
            size: 14,
        },
    },
    quotedPricingGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    quotedPricingText: { weight: "bold" },
    infoRightColumn: {
        width: [12, 12, 12, 6, 3],
        css: css`
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
        `,
    },
    quoteLineButton: {
        css: css`
            min-width: 100%;
            width: 100%;
            margin-bottom: 10px;
        `,
    },
    quoteCalculationModal: { sizeVariant: "small" },
    quoteCalculationContainer: { gap: 20 },
    quoteCalculationHeaderGridItem: { width: 12 },
    quoteCalculationMethodGridItem: { width: [7, 9, 9, 8, 8] },
    quoteCalculationPercentIcon: {
        src: Percent,
        css: css`
            margin: 8px 4px;
        `,
    },
    quoteCalculationPercentGridItem: {
        width: [5, 3, 3, 4, 4],
        align: "bottom",
    },
    quoteCalculationErrorGridItem: { width: 12 },
    quoteCalculationErrorText: { color: "danger" },
    quoteCalculationCancelGridItem: { width: 6 },
    quoteCalculationCancelButton: {
        variant: "secondary",
        css: css`
            width: 100%;
        `,
    },
    quoteCalculationApplyAllGridItem: { width: 6 },
    quoteCalculationApplyAllButton: {
        css: css`
            width: 100%;
        `,
    },
    quoteLineCalculationModal: { sizeVariant: "large" },
};

const styles = rfqQuoteDetailsSalesRepProductListStyles;

const RfqQuoteDetailsSalesRepProductList = ({
    quoteState,
    updateQuote,
    quoteLineForCalculation,
    setQuoteLineForCalculation,
    showPricing,
    updateQuoteLine,
}: Props) => {
    const [quoteCalculationModalIsOpen, setQuoteCalculationModalIsOpen] = useState(false);
    const [quoteCalculationError, setQuoteCalculationError] = useState("");
    const [calculationMethod, setCalculationMethod] = useState(quoteState.value?.calculationMethods?.[0]);
    const [percent, setPercent] = useState(0);
    const [quoteLineCalculationModalIsOpen, setQuoteLineCalculationModalIsOpen] = useState(false);
    /* Verndale customization */
    const [showNotes, setShowNotes] = useState(true);
    const [notesState, setNotesState] = useState<{ [key: string]: string }>({});
    const [addLineItemModalIsOpen, setAddLineItemModalIsOpen] = useState(false);

    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const toasterContext = useContext(ToasterContext);
    /* End verndale customization */
    const quote = quoteState.value;
    if (!quote || !quote.quoteLineCollection) {
        return (
            <StyledWrapper {...styles.spinnerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    useEffect(() => {
        if (quote.quoteLineCollection && quote.quoteLineCollection.length > 0) {
            const newNotesState: { [key: string]: string } = {};
            quote.quoteLineCollection.forEach(quoteLine => {
                newNotesState[quoteLine.id] = quoteLine.notes;
            });
            setNotesState(newNotesState);
        }
    }, [quote.quoteLineCollection]);

    const quoteAllClickHandler = () => {
        setQuoteCalculationModalIsOpen(true);
    };

    const quoteCalculationModalCloseHandler = () => {
        setQuoteCalculationModalIsOpen(false);
    };

    const quoteCalculationModalAfterCloseHandler = () => {
        setQuoteCalculationError("");
        setCalculationMethod(quoteState.value?.calculationMethods?.[0]);
        setPercent(0);
    };

    const calculationMethodChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setCalculationMethod(quoteState.value?.calculationMethods?.find(o => o.name === event.currentTarget.value));
    };

    const percentChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setPercent(parseFloat(event.currentTarget.value));
    };

    const percentBlurHandler = () => {
        setPercent(percent || 0);
    };

    const cancelClickHandler = () => {
        setQuoteCalculationModalIsOpen(false);
    };

    const applyAllClickHandler = () => {
        updateQuote({
            apiParameter: {
                quoteId: quote.id,
                calculationMethod: calculationMethod?.name,
                percent,
            },
            onSuccess: () => {
                setQuoteCalculationModalIsOpen(false);
            },
            onError: error => {
                setQuoteCalculationError(error);
            },
            onComplete(resultProps) {
                if (resultProps.result?.quote) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                } else if (resultProps.result?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onError?.(resultProps.result.errorMessage);
                }
            },
        });
    };

    const quoteLineClickHandler = (quoteLine: QuoteLineModel) => {
        setQuoteLineForCalculation({ quoteLine });
        setQuoteLineCalculationModalIsOpen(true);
    };

    const quoteLineCalculationModalCloseHandler = () => {
        setQuoteLineCalculationModalIsOpen(false);
    };
    /* Verndale customization */
    const toggleNotesHeading = showNotes ? translate("Hide Line Notes") : translate("Show Line Notes");

    const deleteLineQuoteStatus = async (quoteLine: QuoteLineModel, quote: QuoteModel) => {
        const result = await removeQuoteLine(quoteLine.id, quote.id);
        if (result.result === "true") {
            window.location.reload();
        } else if (result.result === "quote deleted") {
            const quoteDetail = window.location.pathname.split("QuoteDetails")[0];
            window.location.replace(quoteDetail);
        }
    };

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>, quoteLineId: string) => {
        setNotesState({
            ...notesState,
            [quoteLineId]: event.currentTarget.value,
        });
    };

    const notesBlurHandler = (quoteLine: QuoteLineModel, event?: React.FormEvent | React.FocusEvent) => {
        const updatedQuoteLine = { ...quoteLine, notes: notesState[quoteLine.id] };
        updateQuoteLineNote({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            Note: notesState[quoteLine.id],
        }).then(data => {
            if (data.result === "Updated") {
                toasterContext.addToast({ body: translate("Notes updated"), messageType: "success" });
            }
        });
    };

    const addLineItemClickHandler = () => {
        setAddLineItemModalIsOpen(true);
    };

    const addLineItemModalCloseHandler = () => {
        setAddLineItemModalIsOpen(false);
    };

    const addLineItemModalSuccessHandler = () => {
        // Reload the page to show the newly added line item
        window.location.reload();
    };
    /* End verndale customization */

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <StyledWrapper {...styles.headerWrapper}>
                <GridContainer {...styles.headerContainer}>
                    <GridItem {...styles.countGridItem}>
                        <Typography {...styles.countValueText}>{quote.quoteLineCollection.length}</Typography>
                        <Typography {...styles.countLabelText}>
                            {quote.quoteLineCollection.length > 1 ? translate("Products") : translate("Product")}
                        </Typography>
                    </GridItem>

                    <GridItem {...styles.quoteAllGridItem}>
                        <Button
                            onClick={addLineItemClickHandler}
                            data-test-selector="add_quoteline_item"
                            {...styles.addQuoteLineItemButton}
                        >
                            {translate("Add Line Item(s)")}
                        </Button>
                        {quote.isEditable && (
                            <Button
                                {...styles.quoteAllButton}
                                onClick={quoteAllClickHandler}
                                data-test-selector="quoteDetails_QuoteAll"
                            >
                                {translate("Quote All")}
                            </Button>
                        )}
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
            {quote.quoteLineCollection.map(quoteLine => (
                <GridContainer
                    key={`${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
                    {...styles.itemContainer}
                    data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
                >
                    <GridItem {...styles.productImageGridItem}>
                        <ProductImage product={quoteLine} extendedStyles={styles.productImage} />
                    </GridItem>
                    <GridItem {...styles.quoteLineInfoCustomGridItem}>
                        <GridContainer {...styles.quoteLineInfoContainer}>
                            <GridItem {...styles.infoLeftColumn}>
                                <GridContainer {...styles.infoLeftColumnContainer}>
                                    <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                        {quoteLine.brand && (
                                            <ProductBrand
                                                brand={quoteLine.brand}
                                                extendedStyles={styles.productBrand}
                                            />
                                        )}
                                        <ProductDescription
                                            product={quoteLine}
                                            extendedStyles={styles.productDescription}
                                        />
                                        <ProductPartNumbers
                                            productNumber={quoteLine.erpNumber}
                                            customerProductNumber={quoteLine.customerName}
                                            manufacturerItem={quoteLine.manufacturerItem}
                                            extendedStyles={styles.productPartNumbers}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.infoCenterColumn}>
                                <GridContainer {...styles.infoContainer}>
                                    <GridItem {...styles.quantityGridItem} data-test-selector="quoteLine_Quantity">
                                        <VisuallyHidden>{translate("Quantity Requested")}</VisuallyHidden>
                                        <SmallHeadingAndText
                                            heading={translate("QTY Req.")}
                                            text={quoteLine.qtyOrdered || 1}
                                            extendedStyles={styles.quantityHeadingAndText}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.uomGridItem}>
                                        {quoteLine.unitOfMeasure && (
                                            <>
                                                <VisuallyHidden>{translate("Unit of Measure")}</VisuallyHidden>
                                                <SmallHeadingAndText
                                                    heading={translate("U/M")}
                                                    text={quoteLine.unitOfMeasure}
                                                    extendedStyles={styles.uomHeadingAndText}
                                                />
                                            </>
                                        )}
                                    </GridItem>
                                    {showPricing && (
                                        <GridItem
                                            {...styles.quotedPricingGridItem}
                                            data-test-selector="quoteAllCalculator_QuotedPricing"
                                        >
                                            <Typography {...styles.quotedPricingText}>
                                                {translate("Quoted Pricing")}
                                            </Typography>
                                            <RfqQuoteDetailsQuotedPricing
                                                quote={quote}
                                                quoteLine={quoteLine}
                                                extendedStyles={styles.quotedPricingStyles}
                                            />
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem>
                            {/* verndale customization */}
                            <GridItem {...styles.infoRightColumn}>
                                {quote.isEditable && showPricing && (
                                    <Button
                                        {...styles.quoteLineButton}
                                        onClick={() => quoteLineClickHandler(quoteLine)}
                                        data-test-selector="quoteDetails_QuoteButton"
                                    >
                                        {translate("Quote")}
                                    </Button>
                                )}
                                {quote.isEditable && showPricing && (
                                    <Button
                                        {...styles.quoteLineButton}
                                        onClick={() => deleteLineQuoteStatus(quoteLine, quote)}
                                        data-test-selector="quoteDetails_DeleteButton"
                                    >
                                        {translate("Delete QuoteLine Item")}
                                    </Button>
                                )}
                            </GridItem>
                            {/* End verndale customization */}
                        </GridContainer>
                        {/* Verndale Customization */}
                        {quoteLine.properties && (
                            <GridContainer {...styles.quoteLineInfoCustomContainer}>
                                {Object.keys(quoteLine.properties)
                                    .filter((key: string) => key.startsWith("file_"))
                                    .map((file: string, index: number) => {
                                        const fileContent: string = quoteLine.properties[file];
                                        const fileContentArray: string[] = fileContent.split(":");
                                        const fileName: string = fileContentArray[0];
                                        const fileUrl: string = fileContentArray[1];
                                        return (
                                            <a
                                                key={index}
                                                href={fileUrl}
                                                target="_blank"
                                                className="file-link"
                                                rel="noreferrer"
                                            >
                                                <span>&bull; {fileName}</span>
                                            </a>
                                        );
                                    })}
                            </GridContainer>
                        )}
                        {/* End Verndale Customization */}
                    </GridItem>
                    {/* Verndale Customization */}

                    <GridItem {...styles.orderLineNotesGridItem}>
                        <Link {...styles.toggleNotesLink} onClick={toggleNotesClickHandler}>
                            {toggleNotesHeading}
                        </Link>
                        {showNotes && (
                            <TextArea
                                {...styles.notesTextArea}
                                value={notesState[quoteLine.id]}
                                disabled={!quote.canModifyOrder}
                                onChange={e => notesChangeHandler(e, quoteLine.id)}
                                onBlur={e => notesBlurHandler(quoteLine, e)}
                            />
                        )}
                    </GridItem>

                    {/* End Verndale Customization */}
                </GridContainer>
            ))}
            <Modal
                {...styles.quoteCalculationModal}
                headline={translate("Quote All")}
                isOpen={quoteCalculationModalIsOpen}
                handleClose={quoteCalculationModalCloseHandler}
                onAfterClose={quoteCalculationModalAfterCloseHandler}
            >
                <GridContainer {...styles.quoteCalculationContainer}>
                    <GridItem {...styles.quoteCalculationHeaderGridItem}>
                        <Typography {...styles.quoteCalculationHeaderText}>
                            {siteMessage("Rfq_QuoteOrderHeader")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.quoteCalculationMethodGridItem}>
                        <Select
                            {...styles.quoteCalculationMethodSelect}
                            label={translate("Discount By")}
                            value={calculationMethod?.name}
                            onChange={calculationMethodChangeHandler}
                            data-test-selector="quoteAllCalculator_QuoteModal_DiscountBy"
                        >
                            {quote.calculationMethods?.map(cm => (
                                <option key={cm.name} value={cm.name}>
                                    {cm.displayName}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.quoteCalculationPercentGridItem}>
                        <TextField
                            {...styles.quoteCalculationPercentTextField}
                            data-test-selector="quoteAllCalculator_QuoteModal_CalculationPercent"
                            type="number"
                            min={calculationMethod?.minimumMargin}
                            max={
                                calculationMethod && parseFloat(calculationMethod.maximumDiscount) > 0
                                    ? parseFloat(calculationMethod.maximumDiscount)
                                    : undefined
                            }
                            value={percent}
                            onChange={percentChangeHandler}
                            onBlur={percentBlurHandler}
                        />
                        <Icon {...styles.quoteCalculationPercentIcon} />
                    </GridItem>
                    {quoteCalculationError && (
                        <GridItem {...styles.quoteCalculationErrorGridItem}>
                            <Typography {...styles.quoteCalculationErrorText}>{quoteCalculationError}</Typography>
                        </GridItem>
                    )}
                    <GridItem {...styles.quoteCalculationCancelGridItem}>
                        <Button
                            {...styles.quoteCalculationCancelButton}
                            onClick={cancelClickHandler}
                            data-test-selector="quoteAllCalculator_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    </GridItem>
                    <GridItem {...styles.quoteCalculationApplyAllGridItem}>
                        <Button
                            {...styles.quoteCalculationApplyAllButton}
                            onClick={applyAllClickHandler}
                            data-test-selector="quoteAllCalculator_apply"
                        >
                            {translate("Apply Quote")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
            <Modal
                {...styles.quoteLineCalculationModal}
                headline={translate("Quote")}
                isOpen={quoteLineCalculationModalIsOpen}
                handleClose={quoteLineCalculationModalCloseHandler}
            >
                {quoteLineForCalculation && (
                    <RfqQuoteDetailsQuoteLineCalculator
                        quote={quote}
                        quoteLine={quoteLineForCalculation}
                        onCancel={quoteLineCalculationModalCloseHandler}
                        onApply={quoteLineCalculationModalCloseHandler}
                    />
                )}
            </Modal>
            <AddQuoteLineModal
                isOpen={addLineItemModalIsOpen}
                quote={quote}
                onClose={addLineItemModalCloseHandler}
                onSuccess={addLineItemModalSuccessHandler}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsSalesRepProductList);
